import React, { useEffect, useState, useCallback } from "react";
import { DriveFolderButton, LinkArea } from "./styles";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import { Loading } from "../Loading/Loading";
import {
  ActionsArea,
  FormItens,
  PageTitle,
} from "../../pages/Announcements/styles";
import { Container } from "../../components/SecretaryTabs/styles";
import { searchStudentsByName } from "../../api/sponteAPI";
import { getFolderUrlIfExists } from "../../api/endpoints/documents";
import { useNotification } from "../../contexts/NotificationContext";

const PASTA_INVALIDA_MESSAGE = "Pasta inválida ou ainda não criada. Salve documentos na aba \"Salvar Documentos\".";

export const SearchDocuments = () => {
  const { showWarning } = useNotification();
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [options, setOptions] = useState([]);
  const [showFolder, setShowFolder] = useState(null);

  // Debounce para busca
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchValue.length >= 3) {
        try {
          setSearchLoading(true);
          const results = await searchStudentsByName(searchValue);

          if (results && Array.isArray(results)) {
            // Formatar para o Autocomplete: "ID - Nome"
            const formattedOptions = results.map(student =>
              `${student.AlunoID} - ${student.Nome}`
            );
            // Remover duplicatas se houver
            setOptions([...new Set(formattedOptions)]);
          } else {
            setOptions([]);
          }
        } catch (error) {
          console.error("Erro na busca de alunos:", error);
          setOptions([]);
        } finally {
          setSearchLoading(false);
        }
      } else {
        setOptions([]);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchValue]);

  // Backend retorna folderUrl só quando a pasta existe no Drive; senão null.
  const searchDocumentsByStudent = useCallback(async (studentValue) => {
    if (studentValue) {
      try {
        setLoading(true);
        setShowFolder(null);
        const AlunoID = studentValue.split(" - ")[0]; // Ajustado para pegar ID antes de " - "
        const { folderUrl } = await getFolderUrlIfExists(AlunoID);
        if (folderUrl) {
          setShowFolder(folderUrl);
        } else {
          showWarning(PASTA_INVALIDA_MESSAGE, "Pasta inválida");
        }
      } catch (error) {
        console.error("Erro ao buscar documentos:", error);
        showWarning(PASTA_INVALIDA_MESSAGE, "Pasta inválida");
      } finally {
        setLoading(false);
      }
    }
  }, [showWarning]);

  const handleSearchChange = useCallback((event, newInputValue) => {
    setSearchValue(newInputValue);
  }, []);

  const handleStudentSelect = useCallback(
    (event, newValue) => {
      if (newValue) {
        setShowFolder(null);
        searchDocumentsByStudent(newValue);
      }
    },
    [searchDocumentsByStudent]
  );

  const handleOpenDriveFolder = useCallback(
    (e) => {
      e.preventDefault();
      if (showFolder) window.open(showFolder, "_blank", "noopener,noreferrer");
    },
    [showFolder]
  );

  return (
    <>
      <Container>
        <PageTitle>Consulta</PageTitle>

        <FormItens encType="multipart/form-data">
          <ActionsArea>
            <Autocomplete
              id="student-search"
              disablePortal
              options={options}
              loading={searchLoading}
              onInputChange={handleSearchChange}
              onChange={handleStudentSelect}
              filterOptions={(x) => x} // Desabilita filtro client-side pois já filtramos no backend
              sx={{ width: "100%" }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Digite o nome do aluno para consulta (mín. 3 caracteres)"
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {searchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />
            {showFolder && (
              <LinkArea>
                <p>Clique na imagem para acessar</p>
                <DriveFolderButton
                  type="button"
                  onClick={handleOpenDriveFolder}
                  aria-label="Abrir pasta do Google Drive em nova aba"
                >
                  <img
                    src="https://www.gstatic.com/images/branding/product/2x/drive_2020q4_48dp.png"
                    alt="Google Drive Folder Icon"
                  />
                </DriveFolderButton>
              </LinkArea>
            )}
          </ActionsArea>
        </FormItens>
      </Container>
      {loading && <Loading />}
    </>
  );
};

export default SearchDocuments;
